import { Check, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const TIERS = [
  {
    id: "starter",
    title: "Starter",
    price: "$2k – $3k",
    tagline: "Estimated completion: 1–2 weeks",
    features: [
      "Modern website with a professional design",
      "Web, tablet, and mobile optimized",
      "Domain connection & hosting setup",
      "Contact forms and lead capture",
      "On-page SEO",
    ],
    highlighted: false,
  },
  {
    id: "business",
    title: "Business",
    price: "$4k – $5k",
    tagline: "Estimated completion: 2–3 weeks",
    features: [
      "Everything in Starter",
      "Additional pages for services and content",
      "Enhanced layouts and visuals",
      "Improved user experience and navigation",
      "Web, tablet, and mobile optimized",
    ],
    highlighted: true,
  },
  {
    id: "premium",
    title: "Premium",
    price: "$6k – $8k",
    tagline: "Estimated completion: 3–4 weeks",
    features: [
      "Everything in Business",
      "Advanced features and functionality",
      "Premium layouts and design",
      "Flexible content for future growth",
      "Built to scale with your business",
    ],
    highlighted: false,
  },
];

export function Pricing() {
  return (
    <section id="pricing" data-testid="pricing-section" className="px-4 sm:px-6 lg:px-12 py-16 md:py-24 lg:py-10 bg-black">
      <div className="max-w-7xl mx-auto lg:px-20">
        <Reveal className="mb-14 md:mb-20 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-amber-400/90 font-medium mb-4">Website Packages</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            Transparent pricing. <span className="text-[#D4AF37]">Serious results.</span>
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-stretch px-3 sm:px-0">
          {TIERS.map((t, i) => (
            <Reveal key={t.id} delay={i * 0.08} className="h-full">
              <div
                data-testid={`pricing-card-${t.id}`}
                className={`relative h-full flex flex-col notched p-8 text-center md:text-left transition-all duration-300 ${
                  t.highlighted
                    ? "border border-amber-400/50 bg-gradient-to-b from-[#1A1D28] to-[#12141C] shadow-[0_0_45px_rgba(212,175,55,0.14)] md:-translate-y-3"
                    : "gold-glow bg-[#12141C]"
                }`}
              >
                {t.highlighted && (
                  <span className="absolute top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-[0.2em] text-black bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] rounded-full px-4 py-1">
                    Most Popular
                  </span>
                )}
                <h3 className={`font-heading text-xl font-semibold ${t.highlighted ? "mt-7" : ""}`}>{t.title}</h3>
                <p className={`mt-3 font-heading text-3xl sm:text-4xl font-bold ${t.highlighted ? "text-[#D4AF37]" : "text-slate-100"}`}>{t.price}</p>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{t.tagline}</p>
                <ul className="mt-7 flex flex-col gap-3 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start justify-center md:justify-start gap-3 text-sm text-slate-300">
                      <Check className="h-4 w-4 mt-0.5 text-amber-400 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <a href="#contact" data-testid={`pricing-cta-${t.id}`}
                  className={`mt-8 inline-flex items-center justify-center gap-2 notched-sm px-6 py-3 text-sm font-semibold transition-all duration-300 ${
                    t.highlighted
                      ? "btn-fill-center border border-amber-400/40 bg-gradient-to-r from-[#F3E5AB] via-[#D4AF37] to-[#B8912A] text-black hover:text-slate-100 hover:shadow-[0_0_30px_rgba(212,175,55,0.4)]"
                      : "btn-fill border border-amber-400/40 bg-transparent text-amber-300 hover:text-black hover:shadow-[0_0_25px_rgba(212,175,55,0.3)]"
                  }`}>
                  Get in Touch
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15} className="mt-14 px-3 sm:px-0">
          <div data-testid="pricing-quote-banner" className="notched border border-white/10 bg-[#12141C] px-6 sm:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-heading text-base sm:text-lg font-semibold">Design, video, social, or ads?</h3>
              <p className="mt-1 text-xs text-slate-400">Graphic Design, Video Editing, AI Video, Social Media & Meta Marketing are tailored per project.</p>
            </div>
            <a href="#contact" data-testid="pricing-quote-cta"
              className="btn-fill notched-sm shrink-0 inline-flex items-center gap-2 border border-amber-400/40 bg-transparent px-5 py-2.5 text-sm font-semibold text-amber-300 hover:text-black transition-[color,box-shadow] duration-300">
              Get in Touch
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
